import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { recipesAPI } from '../../services/api';
import CategoryPicker from '../common/CategoryPicker';
import RichTextEditor from '../common/RichTextEditor';
import { FOOD_CATEGORIES, AVOID_CATEGORIES, CLASSIFICATION_CATEGORIES, CATEGORY_ICONS } from '../../services/constants';
import './AddRecipe.css';

export default function AddRecipePage() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [recipeHtml, setRecipeHtml] = useState('');
  const [notes, setNotes] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [categories, setCategories] = useState([]);
  const [avoids, setAvoids] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);

  // AI Review state
  const [reviewStatus, setReviewStatus] = useState('idle'); // idle | reviewing | approved | rejected | maxFailed
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [reviewAttempts, setReviewAttempts] = useState(0);
  const MAX_REVIEW_ATTEMPTS = 3;

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 3 - images.length);
    setImages(prev => [...prev, ...files]);
    files.forEach(f => {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviews(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(f);
    });
  };

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const toggle = (list, setList, item) => {
    setList(list.includes(item) ? list.filter(i => i !== item) : [...list, item]);
  };

  const handleSubmit = async () => {
    setError('');
    if (name.length < 4 || name.length > 20) { setError('Name must be 4-20 characters'); return; }
    if (description.length < 10 || description.length > 45) { setError('Description must be 10-45 characters'); return; }
    if (categories.length === 0) { setError('Please select at least one category'); return; }
    if (recipeHtml.replace(/<[^>]*>/g, '').trim().length < 120) { setError('Recipe must be at least 120 characters'); return; }

    if (reviewAttempts >= MAX_REVIEW_ATTEMPTS) {
      setReviewStatus('maxFailed');
      return;
    }

    // Step 1: AI Review
    setReviewStatus('reviewing');
    setReviewFeedback('');
    try {
      const reviewResult = await recipesAPI.review({
        name, description, recipeHtml, categories, classifications, notes,
      });

      if (!reviewResult.approved) {
        const attempts = reviewAttempts + 1;
        setReviewAttempts(attempts);
        setReviewFeedback(reviewResult.feedback || 'Please review your recipe and try again.');
        if (attempts >= MAX_REVIEW_ATTEMPTS) {
          setReviewStatus('maxFailed');
        } else {
          setReviewStatus('rejected');
        }
        return;
      }
      setReviewStatus('approved');
    } catch (err) {
      setReviewFeedback('Review service is temporarily unavailable. Please try again.');
      setReviewStatus('rejected');
      const attempts = reviewAttempts + 1;
      setReviewAttempts(attempts);
      if (attempts >= MAX_REVIEW_ATTEMPTS) setReviewStatus('maxFailed');
      return;
    }

    // Step 2: Publish
    setLoading(true);
    const formData = new FormData();
    formData.append('name', name);
    formData.append('description', description);
    formData.append('recipeHtml', recipeHtml);
    formData.append('notes', notes);
    formData.append('isPublic', isPublic);
    formData.append('categories', JSON.stringify(categories));
    formData.append('avoids', JSON.stringify(avoids));
    formData.append('classifications', JSON.stringify(classifications));
    formData.append('mainImageId', '0');
    images.forEach(img => formData.append('images', img));

    try {
      const data = await recipesAPI.create(formData);
      navigate(`/recipe/${data.recipe._id}`);
    } catch (err) {
      setError(err.message);
      setReviewStatus('idle');
    }
    setLoading(false);
  };

  const getPublishButtonClass = () => {
    switch (reviewStatus) {
      case 'reviewing': return 'btn btn-review-pending btn-lg';
      case 'rejected': return 'btn btn-review-retry btn-lg';
      case 'maxFailed': return 'btn btn-review-maxfailed btn-lg';
      default: return 'btn btn-primary btn-lg';
    }
  };

  const getPublishButtonText = () => {
    if (loading) return 'Publishing...';
    switch (reviewStatus) {
      case 'reviewing': return '🔍 Under Review...';
      case 'rejected': return '🔄 Retry Review';
      case 'maxFailed': return '🚫 Maximum Reviews Exceeded';
      default: return '🍳 Publish Recipe';
    }
  };

  return (
    <div className="add-recipe-page">
      <div className="add-recipe-card card">
        <div className="add-recipe-steps">
          {[1, 2, 3].map(s => (
            <div key={s} className={`step-indicator ${s === step ? 'active' : ''} ${s < step ? 'done' : ''}`}>
              {s < step ? '✓' : s}
            </div>
          ))}
        </div>

        {error && <div className="auth-error" style={{ marginBottom: 16 }}>{error}</div>}

        {/* Step 1: Photos */}
        {step === 1 && (
          <div className="add-step fade-in">
            <h2>Add Photos</h2>
            <p className="step-desc">Upload up to 3 photos of your dish</p>
            <div className="image-upload-area">
              {previews.map((src, i) => (
                <div key={i} className="image-preview">
                  <img src={src} alt={`Preview ${i + 1}`} />
                  <button className="image-remove" onClick={() => removeImage(i)}>×</button>
                </div>
              ))}
              {images.length < 3 && (
                <button className="image-add-btn" onClick={() => fileRef.current.click()}>
                  <span>+</span>
                  <small>Add Photo</small>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={handleImageChange} />
            <div className="step-actions">
              <button className="btn btn-primary" onClick={() => setStep(2)}>Next</button>
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div className="add-step fade-in">
            <h2>Recipe Details</h2>
            <div className="form-group">
              <label>Name ({name.length}/20)</label>
              <input className="input-field" placeholder="Recipe name (4-20 chars)" value={name} onChange={e => setName(e.target.value.slice(0, 20))} />
            </div>
            <div className="form-group">
              <label>Description ({description.length}/45)</label>
              <input className="input-field" placeholder="Short description (10-45 chars)" value={description} onChange={e => setDescription(e.target.value.slice(0, 45))} />
            </div>
            <div className="form-group">
              <label className="toggle-label">
                <span>Public Recipe</span>
                <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
                <span className="toggle-switch" />
              </label>
            </div>
            <CategoryPicker label="Categories" items={FOOD_CATEGORIES} selected={categories} onToggle={i => toggle(categories, setCategories, i)} icons={CATEGORY_ICONS} />
            <CategoryPicker label="Free From" items={AVOID_CATEGORIES} selected={avoids} onToggle={i => toggle(avoids, setAvoids, i)} />
            <CategoryPicker label="Dietary" items={CLASSIFICATION_CATEGORIES} selected={classifications} onToggle={i => toggle(classifications, setClassifications, i)} />
            <div className="step-actions">
              <button className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
              <button className="btn btn-primary" onClick={() => setStep(3)}>Next</button>
            </div>
          </div>
        )}

        {/* Step 3: Recipe Text */}
        {step === 3 && (
          <div className="add-step fade-in">
            <h2>Write Your Recipe</h2>
            <div className="form-group">
              <label>Recipe Instructions (min 120 chars)</label>
              <RichTextEditor
                value={recipeHtml}
                onChange={setRecipeHtml}
                placeholder="Write your recipe instructions here..."
              />
            </div>
            <div className="form-group">
              <label>Notes (optional)</label>
              <textarea className="input-field" placeholder="Any additional notes..." value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
            </div>
            <div className="step-actions">
              <button className="btn btn-secondary" onClick={() => setStep(2)}>Back</button>
              <button
                className={getPublishButtonClass()}
                onClick={handleSubmit}
                disabled={loading || reviewStatus === 'reviewing' || reviewStatus === 'maxFailed'}
              >
                {getPublishButtonText()}
              </button>
            </div>
            {reviewFeedback && (
              <div className={`review-feedback ${reviewStatus === 'maxFailed' ? 'review-feedback-error' : 'review-feedback-warning'}`}>
                <strong>📋 AI Review Feedback:</strong> {reviewFeedback}
                {reviewStatus !== 'maxFailed' && (
                  <span className="review-attempts"> (Attempt {reviewAttempts}/{MAX_REVIEW_ATTEMPTS})</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
